<template>
  <div class="age-chooser-wrap">
    <main class="screen" role="main">

      <div class="intro">
        <p class="intro-eyebrow">Swiss Eating Quiz</p>
        <h1 class="intro-title">Willkommen</h1>
        <p class="intro-text">Der Quiz dauert etwa 8 Minuten. Am Ende siehst du dein Ergebnis.</p>
      </div>

      <p class="question-prompt">Wer füllt den Quiz aus?</p>

      <fieldset class="path-fieldset">
        <legend class="sr-only">Auswahl der Person, die den Quiz ausfüllt</legend>
        <div class="options">
          <div v-for="opt in pathOptions" :key="opt.value" class="option">
            <input
              type="radio"
              name="path"
              :id="`path-${opt.value}`"
              :value="opt.value"
              v-model="selectedPath"
            >
            <label :for="`path-${opt.value}`" class="option-label">
              <span class="option-icon" aria-hidden="true">{{ opt.icon }}</span>
              <span class="option-content">
                <span class="option-title">{{ opt.title }}</span>
                <span class="option-description">{{ opt.description }}</span>
              </span>
            </label>
          </div>
        </div>
      </fieldset>

      <div class="privacy-note">
        <span class="privacy-note-icon" aria-hidden="true">🔒</span>
        <p class="privacy-note-text">
          Deine Antworten bleiben anonym. Eine E-Mail-Adresse wird erst am Ende
          abgefragt, wenn du eine Erinnerung in 3 Monaten möchtest.
        </p>
      </div>

      <div class="footer">
        <button
          type="button"
          class="next-button"
          :disabled="!selectedPath"
          @click="goNext"
        >Weiter</button>
      </div>

    </main>
  </div>
</template>

<script>
const NEXT_ROUTE = {
  self: '/consent/adult',
  parent: '/consent/child-parental',
  teacher: '/consent/child-teacher',
};

export default {
  name: 'AgeChooser',
  data() {
    return {
      selectedPath: null,
      pathOptions: [
        {
          value: 'self',
          icon: '👤',
          title: 'Ich mache den Quiz selbst',
          description: 'Ich bin 15 Jahre oder älter.',
        },
        {
          value: 'parent',
          icon: '👨‍👧',
          title: 'Ich fülle den Quiz für mein Kind aus',
          description: 'Mein Kind ist jünger als 15 Jahre.',
        },
        {
          value: 'teacher',
          icon: '🎓',
          title: 'Ich bin Lehrperson',
          description: 'Ich führe den Quiz mit einer Klasse durch.',
        },
      ],
    };
  },
  methods: {
    goNext() {
      const next = NEXT_ROUTE[this.selectedPath];
      if (next) this.$router.push(next);
    },
  },
};
</script>

<style scoped>
.age-chooser-wrap {
  --color-bg-page: #f5f4ef;
  --color-bg-card: #ffffff;
  --color-bg-muted: #f1efe8;
  --color-text-primary: #2c2c2a;
  --color-text-secondary: #5f5e5a;
  --color-text-tertiary: #888780;
  --color-border: rgba(0, 0, 0, 0.15);
  --color-accent: #534AB7;
  --color-accent-light: #EEEDFE;
  --radius-md: 8px;
  --radius-lg: 12px;
  --font-sans: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;

  background: var(--color-bg-page);
  font-family: var(--font-sans);
  color: var(--color-text-primary);
  line-height: 1.5;
  min-height: 100%;
  padding: 1.25rem;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  box-sizing: border-box;
}

.age-chooser-wrap *,
.age-chooser-wrap *::before,
.age-chooser-wrap *::after {
  box-sizing: border-box;
}

.screen {
  width: 100%;
  max-width: 420px;
  background: var(--color-bg-card);
  border-radius: var(--radius-lg);
  border: 0.5px solid var(--color-border);
  padding: 1.5rem 1.25rem;
  min-height: 600px;
  display: flex;
  flex-direction: column;
}

.intro {
  margin-bottom: 2rem;
}

.intro-eyebrow {
  font-size: 13px;
  color: var(--color-text-secondary);
  margin: 0 0 8px;
  letter-spacing: 0.02em;
}

.intro-title {
  font-size: 24px;
  font-weight: 500;
  margin: 0 0 12px;
  line-height: 1.25;
}

.intro-text {
  font-size: 15px;
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.6;
}

.question-prompt {
  font-size: 16px;
  font-weight: 500;
  margin: 0 0 1rem;
  color: var(--color-text-primary);
}

.path-fieldset {
  border: none;
  padding: 0;
  margin: 0;
}

.options {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 1.5rem;
}

.option {
  position: relative;
}

.option input[type="radio"] {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.option-label {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px;
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  cursor: pointer;
  background: var(--color-bg-card);
  transition: background 0.15s, border-color 0.15s;
}

.option-label:hover {
  background: var(--color-bg-muted);
}

.option input:checked + .option-label {
  border-color: var(--color-accent);
  background: var(--color-accent-light);
}

.option-icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--color-bg-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  line-height: 1;
}

.option input:checked + .option-label .option-icon {
  background: var(--color-accent);
  color: white;
}

.option-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.option-title {
  font-size: 15px;
  font-weight: 500;
  margin: 0 0 4px;
  color: var(--color-text-primary);
  line-height: 1.3;
}

.option-description {
  font-size: 13px;
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.5;
}

.privacy-note {
  background: var(--color-bg-muted);
  border-radius: var(--radius-md);
  padding: 12px 14px;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.privacy-note-icon {
  flex-shrink: 0;
  color: var(--color-accent);
  font-size: 16px;
  margin-top: 1px;
}

.privacy-note-text {
  font-size: 13px;
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.5;
}

.footer {
  margin-top: auto;
}

.next-button {
  width: 100%;
  padding: 14px;
  background: var(--color-bg-muted);
  color: var(--color-text-tertiary);
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  font-size: 15px;
  font-family: inherit;
  cursor: not-allowed;
  transition: background 0.15s;
}

.next-button:not(:disabled) {
  background: var(--color-accent);
  color: white;
  border-color: var(--color-accent);
  cursor: pointer;
}

.next-button:not(:disabled):hover {
  opacity: 0.9;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}
</style>
